import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check as CheckIcon } from "lucide-react";

export function FieldError({ message, id }) {
  if (!message) return null;
  return <p id={id} className="mt-1.5 text-[12px] font-medium text-rose-600">{message}</p>;
}

/* ─────────────────────────────────────────────────────────────────────────────
   JourneyGroup — a folder-tab card: the docket, digitised.
   Each group is a file in the shipment's paperwork. Its tab carries the icon,
   the title and the one live signal (blue while typing via :focus-within,
   emerald pop when the group is complete); the body joins seamlessly beneath.
   Silhouette does the hierarchy - no per-card shadows, no offsets.
   Module-scoped: an inline component identity would remount fields per keystroke.
──────────────────────────────────────────────────────────────────────────── */
export default function JourneyGroup({ icon: GIcon, title, hint, right, done, children }) {
  return (
    <section className="group/jg">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className={`relative z-10 -mb-px inline-flex h-10 items-center gap-2.5 rounded-t-xl border border-b-0 px-4 transition-colors duration-200 ${
          done
            ? "border-emerald-200 bg-emerald-50/70"
            : "border-[#E2EAF8] bg-[#F6F9FF] group-focus-within/jg:border-[#3877FC]/35 group-focus-within/jg:bg-[#3877FC]/[0.05]"
        }`}>
          <span
            className={`relative grid h-6 w-6 flex-shrink-0 place-items-center rounded-full transition-all duration-200 ${
              done
                ? ""
                : "bg-[#3877FC]/[0.08] text-[#2A59BD] group-focus-within/jg:bg-[#3877FC]/[0.14] group-focus-within/jg:ring-4 group-focus-within/jg:ring-[#3877FC]/10"
            }`}
          >
            <AnimatePresence initial={false}>
              {done ? (
                <motion.span
                  key="done"
                  initial={{ scale: 0.3, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.3, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 22 }}
                  className="absolute inset-0 grid place-items-center rounded-full bg-emerald-500 text-white ring-[3px] ring-emerald-500/15"
                >
                  <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
                </motion.span>
              ) : null}
            </AnimatePresence>
            <GIcon className={`h-3.5 w-3.5 ${done ? "opacity-0" : ""}`} aria-hidden="true" />
          </span>
          <h3 className={`whitespace-nowrap text-[13.5px] font-semibold tracking-[-0.01em] transition-colors duration-200 ${
            done ? "text-emerald-900" : "text-slate-900 group-focus-within/jg:text-[#2A59BD]"
          }`}>{title}</h3>
        </div>
        {/* Phones: the actions take a full row (a fixed-width search beside the tab clipped);
            desktop: beside the tab as before. */}
        {right ? <div className="mb-1.5 flex w-full items-center gap-2 sm:w-auto">{right}</div> : null}
      </div>
      <div className="rounded-xl rounded-tl-none border border-[#E2EAF8] bg-[#F6F9FF] px-4 pb-3.5 pt-3 lg:px-5">
        {hint ? <p className="-mt-0.5 mb-3.5 text-[12.5px] text-slate-500">{hint}</p> : null}
        {children}
      </div>
    </section>
  );
}
